import { useEffect, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { saveAs } from 'file-saver';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PreviewFile, getAssetType } from '@frontend/media-upload-preview';
import { PdfPreview } from '@frontend/messaging-media-lightbox';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Modal, Text, useModalControl, useTooltip } from '@frontend/design-system';
import { PdfPageThumbnail } from '../../common/pdf-page-thumbnail/pdf-page-thumbnail';
import { DownloadButton } from './actions-bar-popover/download-all-action';

interface AttachmentProps extends TeamChatTypes.Attachment {
  url: string;
  onOpenAttachment?: () => void;
  isEnhancedAttachmentsEnabled: boolean;
}

interface PdfAttachmentProps extends Omit<AttachmentProps, 'isEnhancedAttachmentsEnabled'> {
  title: string;
}

interface AttachmentsProps extends Pick<AttachmentProps, 'onOpenAttachment'> {
  attachments: TeamChatTypes.Attachment[];
  isEnhancedAttachmentsEnabled: boolean;
}

const PdfAttachment = ({ url, title }: PdfAttachmentProps) => {
  const { t } = useTranslation('team-chat');
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({});

  return (
    <>
      <figure
        className='pdf'
        onClick={() => setShowPdf(true)}
        tabIndex={0}
        role='button'
        aria-label={t('Open {{file}}', { file: title })}
      >
        <PdfPageThumbnail src={url} css={styles.pdfThumbnail} />
        <div css={styles.pdfInfo}>
          <Text size='small' weight='bold' color='white' {...triggerProps} css={styles.pdfTitle}>
            {title}
          </Text>
          <div css={styles.pdfMeta}>
            <Text size='small' color='white'>
              {t('PDF')}
            </Text>
            <Tooltip {...tooltipProps}>{title}</Tooltip>
            <DownloadButton
              url={url}
              title={title}
              show
              trackingId='team-chat-2.0-download-pdf-thumbnail'
              css={styles.pdfDownloadButton}
            />
          </div>
        </div>
      </figure>

      <PdfPreview
        src={url}
        filename={title}
        show={showPdf}
        onClose={() => setShowPdf(false)}
        onDownload={() => saveAs(url, title)}
      />
    </>
  );
};

const Attachment = ({ onOpenAttachment, url, title, type, isEnhancedAttachmentsEnabled }: AttachmentProps) => {
  const { t } = useTranslation('team-chat');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const { closeModal, modalProps, triggerProps } = useModalControl();
  const isGif = type === 'giphy';
  const showDownloadButton = !isLoading && !error && !isGif && isEnhancedAttachmentsEnabled;

  useEffect(() => {
    if (modalProps.show && onOpenAttachment) {
      onOpenAttachment();
    }
  }, [modalProps.show, onOpenAttachment]);

  return (
    <>
      <figure>
        {url && !error ? (
          <img
            {...(isLoading ? {} : triggerProps)}
            className='attachment-img'
            onError={() => {
              setError(true);
              setIsLoading(false);
            }}
            onLoad={() => {
              setIsLoading(false);
            }}
            src={url}
          />
        ) : (
          <>
            <Text color='error' size='small' style={{ padding: 'var(--fabric-space-padding-md)' }}>
              {t('There was an error loading this image.')}
            </Text>
          </>
        )}
        <ContentLoader size='small' show={isLoading} />
        <DownloadButton
          url={url}
          title={title}
          show={showDownloadButton}
          trackingId='team-chat-2.0-download-image-thumbnail'
          css={styles.imageDownloadButton}
        />
      </figure>

      {url && (
        <Modal {...modalProps} css={styles.modal}>
          <div css={styles.modalActions}>
            <DownloadButton url={url} title={title} show={!isGif} trackingId='team-chat-2.0-download-preview-image' />
            <Button
              label={t('Close')}
              onClick={closeModal}
              variant='secondary'
              size='small'
              trackingId='team-chat-v2.0-close-preview-image'
            >
              <Icon name='x-small' />
            </Button>
          </div>

          <img src={url} />
        </Modal>
      )}
    </>
  );
};

export const Attachments = ({ attachments, ...rest }: AttachmentsProps) => {
  const memoizedAttachments = useMemo(() => {
    const { images, pdfs, otherFiles } = attachments.reduce<{
      images: TeamChatTypes.Attachment[];
      pdfs: TeamChatTypes.Attachment[];
      otherFiles: TeamChatTypes.Attachment[];
    }>(
      (acc, attachment) => {
        if (attachment.type === 'image' || attachment.type === 'giphy') {
          acc.images.push(attachment);
        } else if (attachment.type === 'pdf' && rest.isEnhancedAttachmentsEnabled) {
          acc.pdfs.push(attachment);
        } else if (attachment.type === 'docx' && rest.isEnhancedAttachmentsEnabled) {
          acc.otherFiles.push(attachment);
        }
        return acc;
      },
      { images: [], pdfs: [], otherFiles: [] }
    );
    // This is to re-arrange the attachments in order of arrangements
    return [...images, ...pdfs, ...otherFiles];
  }, [attachments, rest.isEnhancedAttachmentsEnabled]);

  return (
    <div css={styles.wrapper}>
      {memoizedAttachments.map((attachment, index) => {
        // Not using the ChatItem.Image as it needs ChatItemContext which is not available out of the box.
        // To get the context, we need to wrap it in ChatItem component.
        // The ChatItem component has it's own styles and structure (so overriding will be required).
        // and it also adds additional html elements on the view which are not required for the revamped UI.
        switch (attachment.type) {
          case 'pdf':
            return <PdfAttachment key={index} {...rest} {...attachment} />;
          case 'image':
          case 'giphy':
            return <Attachment {...rest} key={index} {...attachment} />;
          case 'docx':
            return (
              <PreviewFile
                media={{
                  id: attachment.title,
                  uploading: false,
                  hasError: false,
                  previewSrc: attachment.url,
                  mediaObj: {
                    filename: attachment.title,
                    url: attachment.url,
                    mediaId: attachment.url,
                    mimetype: getAssetType(attachment.type),
                  },
                }}
                assetKind={attachment.type}
                isRemovable={false}
                maxWidth='100%'
                className='file'
                key={attachment.url}
                showDownloadButton
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

const styles = {
  wrapper: css`
    border-radius: var(--fabric-radius-md);
    display: flex;
    gap: var(--fabric-space-gap-sm);
    margin-top: var(--fabric-space-gap-sm);
    overflow: auto;
    flex-flow: row wrap;
    align-items: baseline;

    figure {
      border-radius: var(--fabric-card-radius);
      border: 1px solid var(--fabric-card-border);
      min-height: ${theme.spacing(5)};
      min-width: calc(50% - (var(--fabric-space-gap-xs)));
      width: calc(50% - (var(--fabric-space-gap-xs)));
      overflow: hidden;
      position: relative;
      height: 50%;

      .attachment-img {
        border-radius: var(--fabric-card-radius);
        cursor: pointer;
        height: 100%;
        object-fit: cover;
        width: 100%;
        aspect-ratio: 1/1;
      }
    }
    figure.image {
      min-width: calc(50% - (var(--fabric-space-gap-sm) / 2));
      width: calc(50% - (var(--fabric-space-gap-sm) / 2));
      background-color: var(--fabric-card-background);
    }

    figure.pdf {
      aspect-ratio: 1/1;
      cursor: pointer;
      height: auto;
    }

    figure.file {
      width: 100%;
      padding: var(--fabric-space-padding-sm) 0;
      background-color: 'var(--fabric-color-neutral-alpha-100-alpha-5)';
      border: none;
    }
  `,

  // Fills the figure so the first PDF page acts as the thumbnail; the pdfInfo bar overlays the bottom.
  pdfThumbnail: css`
    background-color: var(--fabric-color-neutral-00);
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;

    canvas {
      width: 100%;
      height: auto;
    }
  `,

  pdfInfo: css`
    align-items: flex-start;
    background-color: var(--fabric-color-surface-overlay-inverse);
    bottom: 0;
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xxs);
    left: 0;
    padding: var(--fabric-space-padding-sm);
    position: absolute;
    width: 100%;
  `,

  pdfTitle: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  `,

  pdfMeta: css`
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
  `,

  pdfDownloadButton: css`
    background-color: white;
    padding: var(--fabric-space-padding-xxs);
  `,

  imageDownloadButton: css`
    position: absolute;
    top: var(--fabric-space-gap-xs);
    right: var(--fabric-space-gap-xs);
  `,

  modal: css`
    max-width: unset;
    padding: 0;
    background-color: transparent;

    img {
      max-height: 90vh;
      max-width: 70vw;
      padding: 0;
    }
  `,

  modalActions: css`
    align-items: center;
    display: flex;
    justify-content: flex-end;
    margin-bottom: var(--fabric-space-gap-sm);
    gap: var(--fabric-space-gap-sm);

    > button {
      background-color: var(--fabric-color-neutral-00);
      padding: var(--fabric-space-padding-xxs);
    }
  `,
};
